import { createAxios } from '../lib/axiosFactory';
import { errorParser } from '../lib/response/errorParser';
import { ActionResponse } from '../lib/interfaces/actionResponse';

export const state = {
  shopify: {
    domain: undefined,
    status: undefined,
    syncCommunicationPreferenceToShopify: undefined,
    syncContactToShopify: undefined,
    listIdsToBeAdded: undefined,
    shopName: undefined
  }
};

export const getters = {
  isConnectedToShopify(state) {
    return state.shopify.status === 'connected';
  },

  // A separate getter to 'isConnectedToShopify' as opposed to the inverse
  // as this would return false initially until data is loaded as opposed to
  // the other way round. This is useful for ensuring correct UI loading behaviour.
  // For all other purposes, prefer '!isConnectedToShopify'.
  isNotConnectedToShopify(state) {
    return ['disconnected', 'uninstalled'].includes(state.shopify.status);
  }
};

export const mutations = {
  setShopify(state: any, shopify: any) {
    state.shopify.domain = shopify?.domain;
    state.shopify.status = shopify?.status;
    state.shopify.syncCommunicationPreferenceToShopify =
      shopify?.syncCommunicationPreferenceToShopify;
    state.shopify.syncContactToShopify = shopify?.syncContactToShopify;
    state.shopify.listIdsToBeAdded = shopify.listIdsToBeAdded;
    state.shopify.shopName = shopify.shopName;
  },

  setStatus(state: any, status: string) {
    state.shopify.status = status;
  }
};

export const actions = {
  async loadAppIntegrations({ commit }): Promise<ActionResponse> {
    try {
      const response = await createAxios(process.env.AppIntegrationsApiUrl).get(
        '/app-integrations/app-integrations-show'
      );

      const shopify = response.data.data.attributes.shopify || {};

      commit('setShopify', shopify);
      return {
        success: true,
        errors: {},
        body: response.data.data.attributes
      };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },

  async disconnect({ commit }): Promise<ActionResponse> {
    try {
      const response: any = await createAxios(
        process.env.AppIntegrationsApiUrl
      ).patch('/app-integrations/shopify-connections-disconnect');

      commit('setStatus', response.data.data.attributes.status);
      return {
        success: true,
        errors: {},
        body: response.data.data.attributes
      };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },

  async updateAppIntegrations(
    { commit },
    payload: Record<string, unknown>
  ): Promise<ActionResponse> {
    try {
      const response: any = await createAxios(
        process.env.AppIntegrationsApiUrl
      ).patch('/app-integrations/app-integrations-update', {
        data: {
          type: 'appIntegrations',
          attributes: { shopify: payload }
        }
      });
      commit('setShopify', response.data.data.attributes.shopify);
      return {
        success: true,
        errors: {},
        body: response.data.data.attributes
      };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },

  async installShopify({ commit }, { shop }): Promise<ActionResponse> {
    try {
      const response = await createAxios(process.env.AppIntegrationsApiUrl).get(
        '/app-integrations/shopify-install',
        {
          params: {
            shop,
            format: 'json'
          }
        }
      );

      return {
        success: true,
        errors: {},
        body: response.data
      };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  }
};
